import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ReusableTable, { TableColumn, TableAction } from '../components/adminTable';
import {
    accessorService,
    dashboardService,
    ReviewerAssignmentItem,
    ReviewedManuscriptForReReviewItem,
    ReviewerDashboardStats,
    GetReviewerAssignmentPayload,
} from '../services/adminAuth';
import { FileText, CheckCircle, MoreVertical, RotateCw, Clock, AlertTriangle } from 'lucide-react';
import Pagination from '../components/Pagination';
import { Modal } from '../components/adminModals';
import { formatDateDisplay } from '../../../utils/dateHelper';

type Tab = 'my' | 'returned';

const ManuscriptReviewList: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const [activeTab, setActiveTab] = useState<Tab>(searchParams.get('tab') === 'returned' ? 'returned' : 'my');

    // ── My Assignments tab state ─────────────────────────────────────────────
    const [manuscripts, setManuscripts] = useState<ReviewerAssignmentItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    // ── Dashboard stats ──────────────────────────────────────────────────────
    const [stats, setStats] = useState<ReviewerDashboardStats | null>(null);

    // ── Conflict of Interest notes modal ─────────────────────────────────────
    const [conflictTarget, setConflictTarget] = useState<ReviewerAssignmentItem | null>(null);

    // ── Returned Reviewed Manuscripts tab state ──────────────────────────────
    const [returnedItems, setReturnedItems] = useState<ReviewedManuscriptForReReviewItem[]>([]);
    const [loadingReturned, setLoadingReturned] = useState(false);
    const [returnedSearch, setReturnedSearch] = useState('');
    const [returnedPage, setReturnedPage] = useState(1);
    const [returnedPageSize, setReturnedPageSize] = useState(10);
    const [returnedTotalRecords, setReturnedTotalRecords] = useState(0);
    const [returnedTotalPages, setReturnedTotalPages] = useState(0);

    // ── Fetchers ─────────────────────────────────────────────────────────────

    const fetchManuscripts = useCallback(async () => {
        setLoading(true);
        try {
            const payload: GetReviewerAssignmentPayload = {
                pageNumber: currentPage,
                pageSize,
                sortBy: '',
                sortDescending: true,
            };
            const res = await accessorService.getReviewerAssignment(payload);
            if (res.isSuccess && res.data) {
                setManuscripts(res.data.data);
                setTotalRecords(res.data.totalRecords);
                setTotalPages(res.data.totalPages);
            }
        } catch {
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize]);

    const fetchReturned = useCallback(async () => {
        setLoadingReturned(true);
        try {
            const payload: GetReviewerAssignmentPayload = {
                pageNumber: returnedPage,
                pageSize: returnedPageSize,
                sortBy: '',
                sortDescending: true,
            };
            const res = await accessorService.getReviewedManuscriptForReReview(payload);
            if (res.isSuccess && res.data) {
                setReturnedItems(res.data.data);
                setReturnedTotalRecords(res.data.totalRecords);
                setReturnedTotalPages(res.data.totalPages);
            }
        } catch {
        } finally {
            setLoadingReturned(false);
        }
    }, [returnedPage, returnedPageSize]);

    const fetchStats = useCallback(async () => {
        try {
            const res = await dashboardService.getReviewerDashboardStatistics();
            if (res.isSuccess && res.data) setStats(res.data);
        } catch {
            /* leave stats null on failure */
        }
    }, []);

    useEffect(() => {
        fetchManuscripts();
    }, [fetchManuscripts]);

    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    useEffect(() => {
        if (activeTab === 'returned') fetchReturned();
    }, [activeTab, fetchReturned]);

    // ── Handlers ─────────────────────────────────────────────────────────────

    const handleReviewForm = (m: ReviewerAssignmentItem) => {
        navigate(`/admin/review-form/${m.reviewerAssignmentId}`);
    };

    const handleReReview = (row: ReviewedManuscriptForReReviewItem) => {
        navigate(`/admin/review-form/${row.reviewAssignmentId}`);
    };

    const openConflictModal = (row: ReviewerAssignmentItem) => setConflictTarget(row);
    const closeConflictModal = () => setConflictTarget(null);

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' });
    };

    const getStatusBadge = (row: ReviewerAssignmentItem) => {
        if (row.isCompleted) {
            return (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Completed
                </span>
            );
        }
        return (
            <span className="text-sm font-medium text-gray-700 whitespace-nowrap">
                {row.status === 'UnderReview' ? 'Under Review' : row.status}
            </span>
        );
    };

    // ── My Assignments columns/actions ──────────────────────────────────────

    const columns: TableColumn<ReviewerAssignmentItem>[] = [
        {
            key: 'submissionNumber',
            label: 'Submission ID',
            render: (value) => <span className="font-bold text-gray-900">{value}</span>,
        },
        {
            key: 'title',
            label: 'Manuscript Title',
            className: 'max-w-[350px]',
            render: (value) => (
                <div className="font-medium text-gray-700 leading-snug truncate" title={value}>
                    {value}
                </div>
            ),
        },
        {
            key: 'applicationType',
            label: 'Application Type',
            render: (value) => <span className="text-gray-500 text-sm">{value}</span>,
        },
        {
            key: 'assignedAt',
            label: 'Assigned Date',
            render: (value) => <span className="text-gray-400 text-xs font-medium">{formatDate(value)}</span>,
        },
        {
            key: 'status',
            label: 'Status',
            render: (_, row) => getStatusBadge(row),
        },
        {
            key: 'conflictOfInterest',
            label: 'Conflict of Interest',
            render: (_, row) =>
                row.conflictOfInterest ? (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-50 text-red-700 border border-red-200">
                        <AlertTriangle className="w-3.5 h-3.5" />
                        Yes
                    </span>
                ) : (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-50 text-green-700 border border-green-200">
                        <CheckCircle className="w-3.5 h-3.5" />
                        No
                    </span>
                ),
        },
    ];

    const actions: TableAction<ReviewerAssignmentItem>[] = [
        {
            icon: <MoreVertical className="w-5 h-5" />,
            onClick: () => undefined,
            title: 'Actions',
            className: 'text-gray-600 hover:text-gray-900',
            variant: 'dropdown',
            dropdownItems: [
                {
                    label: (row) => (row.isCompleted ? 'View Review' : 'Review'),
                    icon: <FileText className="w-4 h-4 text-green-600" />,
                    onClick: (row) => handleReviewForm(row),
                    className: (row) => (row.conflictOfInterest ? 'hidden' : ''),
                },
                {
                    label: 'View Conflict Notes',
                    icon: <AlertTriangle className="w-4 h-4 text-red-600" />,
                    onClick: (row) => openConflictModal(row),
                    className: (row) => (row.conflictOfInterest ? 'text-red-600' : 'hidden'),
                },
            ],
        },
    ];

    // ── Returned Reviewed Manuscripts columns/actions ───────────────────────

    const returnedColumns: TableColumn<ReviewedManuscriptForReReviewItem>[] = [
        {
            key: 'submissionNumber',
            label: 'Submission ID',
            render: (value) => <span className="font-bold text-gray-900">{value}</span>,
        },
        {
            key: 'title',
            label: 'Manuscript Title',
            className: 'max-w-[300px]',
            render: (value) => (
                <div className="font-medium text-gray-700 leading-snug truncate" title={value}>
                    {value}
                </div>
            ),
        },
        {
            key: 'categoryName',
            label: 'Thematic Area',
            render: (value) => <span className="text-gray-600 text-sm">{value}</span>,
        },
        {
            key: 'reason',
            label: 'Chairman Reason',
            className: 'max-w-[260px]',
            render: (value: string) => (
                <span className="text-xs text-gray-500 italic block truncate" title={value}>
                    {value || '—'}
                </span>
            ),
        },
        {
            key: 'totalScore',
            label: 'Previous Score',
            render: (value: number) => (
                <span className="text-sm font-semibold text-gray-700">{value ?? '—'}</span>
            ),
        },
        {
            key: 'deadLine',
            label: 'Deadline',
            render: (value: string) => (
                <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 px-2 py-1 rounded">
                    <Clock className="w-3 h-3" />
                    {formatDateDisplay(value)}
                </span>
            ),
        },
    ];

    const returnedActions: TableAction<ReviewedManuscriptForReReviewItem>[] = [
        {
            icon: <MoreVertical className="w-5 h-5" />,
            onClick: () => undefined,
            title: 'Actions',
            className: 'text-gray-600 hover:text-gray-900',
            variant: 'dropdown',
            dropdownItems: [
                {
                    label: 'Re-Review',
                    icon: <RotateCw className="w-4 h-4 text-[#088A61]" />,
                    onClick: (row) => handleReReview(row),
                },
            ],
        },
    ];

    // ── Client-side filtering on My Assignments (kept from original) ─────────
    const filteredManuscripts = React.useMemo(() => {
        if (!searchQuery.trim()) return manuscripts;
        const lowQuery = searchQuery.toLowerCase();
        return manuscripts.filter(
            (m) =>
                m.title.toLowerCase().includes(lowQuery) ||
                m.applicationType.toLowerCase().includes(lowQuery)
        );
    }, [manuscripts, searchQuery]);

    const filteredReturned = React.useMemo(() => {
        if (!returnedSearch.trim()) return returnedItems;
        const lowQuery = returnedSearch.toLowerCase();
        return returnedItems.filter(
            (r) =>
                r.title.toLowerCase().includes(lowQuery) ||
                r.submissionNumber.toLowerCase().includes(lowQuery) ||
                r.categoryName.toLowerCase().includes(lowQuery)
        );
    }, [returnedItems, returnedSearch]);

    const totalAssigned = stats?.totalAssignment ?? totalRecords;
    const pendingCount = stats?.totalPending ?? manuscripts.filter((m) => !m.isCompleted).length;
    const completedCount = stats?.totalReviewed ?? manuscripts.filter((m) => m.isCompleted).length;
    const returnedBadge = stats?.totalReturnedManuscript ?? 0;

    return (
        <div className="w-full">
            <div className="mb-10">
                <h1 className="text-3xl font-black text-gray-900 mb-2">Manuscript Reviews</h1>
                <p className="text-gray-500 font-medium">
                    Evaluate and provide feedback on assigned research manuscripts.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
                <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm transition-transform hover:scale-[1.02]">
                    <div className="flex items-center justify-between mb-4">
                        <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">Total Assigned</span>
                        <div className="bg-gray-50 p-2 rounded-lg">
                            <FileText className="w-5 h-5 text-gray-400" />
                        </div>
                    </div>
                    <div className="text-4xl font-black text-gray-900">{totalAssigned}</div>
                </div>
                <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm transition-transform hover:scale-[1.02]">
                    <div className="flex items-center justify-between mb-4">
                        <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">Pending Review</span>
                        <div className="bg-blue-50 p-2 rounded-lg">
                            <div className="w-2.5 h-2.5 rounded-full bg-blue-500 animate-pulse" />
                        </div>
                    </div>
                    <div className="text-4xl font-black text-blue-600">{pendingCount}</div>
                </div>
                <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm transition-transform hover:scale-[1.02]">
                    <div className="flex items-center justify-between mb-4">
                        <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">Completed</span>
                        <div className="bg-green-50 p-2 rounded-lg">
                            <CheckCircle className="w-5 h-5 text-green-500" />
                        </div>
                    </div>
                    <div className="text-4xl font-black text-green-600">{completedCount}</div>
                </div>
            </div>

            {/* Tabs */}
            <div className="bg-gray-100 p-1 rounded-xl w-full mb-6">
                <div className="flex gap-1 w-full">
                    {([
                        { key: 'my', label: 'My Assignments', badge: null as number | null },
                        {
                            key: 'returned',
                            label: 'Returned Reviewed Manuscripts',
                            badge: returnedBadge > 0 ? returnedBadge : null,
                        },
                    ] as { key: Tab; label: string; badge: number | null }[]).map((tab) => (
                        <button
                            key={tab.key}
                            onClick={() => setActiveTab(tab.key)}
                            className={`flex-1 relative px-4 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 flex items-center justify-center gap-2 ${
                                activeTab === tab.key
                                    ? 'bg-white text-gray-900 shadow-sm ring-1 ring-black/5'
                                    : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                            }`}
                        >
                            {tab.label}
                            {tab.badge != null && (
                                <span
                                    className={`inline-flex items-center justify-center px-1.5 py-0.5 rounded-full text-xs font-semibold transition-colors ${
                                        activeTab === tab.key
                                            ? 'bg-orange-500 text-white'
                                            : 'bg-orange-100 text-orange-700'
                                    }`}
                                >
                                    {tab.badge}
                                </span>
                            )}
                        </button>
                    ))}
                </div>
            </div>

            {/* My Assignments */}
            {activeTab === 'my' && (
                <>
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 relative overflow-x-auto">
                        <ReusableTable
                            columns={columns}
                            data={filteredManuscripts}
                            actions={actions}
                            loading={loading}
                            onSearch={setSearchQuery}
                            searchPlaceholder="Search by title, type..."
                            emptyMessage="No manuscripts assigned for review."
                        />
                    </div>
                    {!loading && totalRecords > 0 && (
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            totalRecords={totalRecords}
                            pageSize={pageSize}
                            onPageChange={(p) => setCurrentPage(p)}
                            onPageSizeChange={(s) => {
                                setPageSize(s);
                                setCurrentPage(1);
                            }}
                        />
                    )}
                </>
            )}

            {/* Returned Reviewed Manuscripts */}
            {activeTab === 'returned' && (
                <>
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 relative overflow-x-auto">
                        <ReusableTable
                            columns={returnedColumns}
                            data={filteredReturned}
                            actions={returnedActions}
                            loading={loadingReturned}
                            onSearch={setReturnedSearch}
                            searchPlaceholder="Search by title, ID, thematic area..."
                            emptyMessage="No manuscripts returned for re-review."
                        />
                    </div>
                    {!loadingReturned && returnedTotalRecords > 0 && (
                        <Pagination
                            currentPage={returnedPage}
                            totalPages={returnedTotalPages}
                            totalRecords={returnedTotalRecords}
                            pageSize={returnedPageSize}
                            onPageChange={(p) => setReturnedPage(p)}
                            onPageSizeChange={(s) => {
                                setReturnedPageSize(s);
                                setReturnedPage(1);
                            }}
                        />
                    )}
                </>
            )}

            {/* ── Conflict of Interest Notes Modal ── */}
            <Modal
                isOpen={conflictTarget !== null}
                onClose={closeConflictModal}
                title="Conflict of Interest"
                description="The reviewer declared a conflict of interest for this manuscript."
                maxWidth="md"
            >
                <div className="space-y-4 mt-2">
                    {conflictTarget && (
                        <div className="bg-gray-50 rounded-lg px-4 py-3 border border-gray-100">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">
                                Manuscript
                            </p>
                            <p className="text-sm font-medium text-gray-900 line-clamp-2">{conflictTarget.title}</p>
                            <p className="text-xs text-gray-400 mt-0.5 font-mono">{conflictTarget.submissionNumber}</p>
                        </div>
                    )}

                    <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                        <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                        <div>
                            <p className="text-xs font-semibold text-red-700 uppercase tracking-wide mb-1">Reason</p>
                            <p className="text-sm text-gray-700 whitespace-pre-wrap">
                                {conflictTarget?.conflictOfInterestReason?.trim() || 'No reason provided.'}
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center justify-end pt-2">
                        <button
                            onClick={closeConflictModal}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default ManuscriptReviewList;
